import { BookingInfo } from "@/app/[bookingType]/context/bookingProvider";
import {
  PaymentMethod,
  TravelProfile,
  TravelProfileResponse,
  UserTravelProfilesPostRequest,
} from "@/generated/email_parser.openapi";
import { handleError, reportError } from "@/lib/apiUtils";

export type PersonalDetails = {
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber?: string;
};

export const fetchPaymentMethods = async (): Promise<PaymentMethod[]> => {
  const methods = await fetch("/api/user/payment_method/get").then((res) =>
    res.json()
  );
  return methods.sort((a: PaymentMethod) => (a._default ? -1 : 1));
};

export const fetchTravelerInfo = async (): Promise<
  TravelProfileResponse | undefined
> => {
  try {
    const response = (await fetch("/api/user/traveler/get").then((res) =>
      res.json()
    )) as TravelProfileResponse;
    return response;
  } catch (error) {
    handleError("Failed to fetch traveler info", {
      reportError: true,
      silent: true,
    });
    return undefined;
  }
};

export const createTravelerInfo = async (
  contactInfo: PersonalDetails,
  userId: string
): Promise<void> => {
  const request: UserTravelProfilesPostRequest = {
    travelProfile: {
      email: contactInfo.emailAddress,
      firstName: contactInfo.firstName,
      lastName: contactInfo.lastName,
      phoneNumber: contactInfo.phoneNumber,
      userId: userId,
    },
  };

  await fetch("/api/user/traveler/add", {
    method: "POST",
    body: JSON.stringify(request),
  });
};

export const parseTravelerInfoToContactInfo = (
  travelerInfo: TravelProfile,
  defaultEmail: string = "",
  defaultFirstName: string = "",
  defaultLastName: string = ""
): PersonalDetails => {
  return {
    firstName: defaultFirstName || travelerInfo.firstName || "",
    lastName: defaultLastName || travelerInfo.lastName || "",
    emailAddress: defaultEmail || travelerInfo.email || "",
    phoneNumber: travelerInfo.phoneNumber || "",
  };
};

export const validateContactInfo = (contactInfo: PersonalDetails): string => {
  if (!contactInfo.firstName) return "First name is required.";
  if (!contactInfo.lastName) return "Last name is required.";
  if (!contactInfo.emailAddress) return "Email address is required.";

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(contactInfo.emailAddress))
    return "Valid email address is required.";

  if (!contactInfo.phoneNumber) return "Phone number is required.";
  return "";
};

export const handleContactInfoChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  prevState: PersonalDetails
): PersonalDetails => {
  const { name, value } = e.target;

  if (name === "firstName" || name === "lastName") {
    return {
      ...prevState,
      [name]: value
        ? new RegExp("^[a-zA-Z0-9\\-\\s]*$").test(value)
          ? value
          : prevState[name]
        : "",
    };
  }

  return { ...prevState, [name]: value };
};

/**
 * Fetches the customer's IP address using the ipify.org API
 * @returns Promise that resolves to the customer's IP address or null if there was an error
 */
export const getCustomerIp = async (): Promise<string | null> => {
  try {
    const response = await fetch("https://api.ipify.org/?format=json");
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    return data.ip;
  } catch (error) {
    reportError(error);
    return null;
  }
};

export const buildOccupancyString = (bookingInfo: BookingInfo) => {
  const adults = bookingInfo?.numberOfAdults;
  const children = bookingInfo?.childrenAges?.length || 0;
  let occupancy = "";
  if (adults || children) {
    const parts = [];
    if (adults) parts.push(`${adults} Adult${adults > 1 ? "s" : ""}`);
    if (children) parts.push(`${children} Child${children > 1 ? "ren" : ""}`);
    occupancy = parts.join(", ");
  }
  return occupancy;
};
